import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Mail, MapPin, Linkedin, Twitter, Github, LifeBuoy } from "lucide-react";
import { useState, useEffect } from "react";

type LayoutProps = {
  children: React.ReactNode | ((props: { openVisionPanel: () => void }) => React.ReactNode);
};

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visionPanelOpen, setVisionPanelOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/products", label: "Products" },
    { path: "/about", label: "About" },
    { path: "/associates", label: "Associates" },
    { path: "/join-us", label: "Careers" },
    { path: "/beta-program", label: "Beta Program" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-background/85 backdrop-blur-lg border-b border-border shadow-[0_1px_0_0_hsl(var(--border))]"
            : "bg-background/70 backdrop-blur-md border-b border-transparent"
        }`}
        data-testid="nav-main"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-[72px]">
            <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="link-home-logo">
              <img
                src="/WNTPL_Brief_Logo.svg"
                alt="Wellnext"
                className="h-10 md:h-11 w-auto object-contain"
              />
              <img
                src="/WNTPL_Text_Logo.svg"
                alt="Wellnext Technologies"
                className="block h-7 sm:h-9 md:h-10 w-auto max-w-[140px] sm:max-w-none object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative px-3.5 py-2 text-sm font-medium rounded-md transition-colors ${
                      active
                        ? "text-[#264653]"
                        : "text-foreground/70 hover:text-[#264653] hover:bg-muted/60"
                    }`}
                    data-testid={`link-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.label}
                    {active && (
                      <span className="absolute left-3.5 right-3.5 -bottom-0.5 h-0.5 rounded-full bg-[#264653]" />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 -mr-2 text-foreground/80"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="button-mobile-menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-1 animate-fade-in">
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                      active
                        ? "text-[#264653] bg-muted"
                        : "text-foreground/70 hover:text-[#264653] hover:bg-muted/60"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`link-mobile-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {typeof children === "function"
          ? children({ openVisionPanel: () => setVisionPanelOpen(true) })
          : children}
      </main>

      {/* Vision Panel Overlay */}
      {visionPanelOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300"
          onClick={() => setVisionPanelOpen(false)}
        />
      )}

      {/* Vision Side Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[500px] bg-background shadow-2xl z-50 transform transition-transform duration-500 ease-in-out ${
          visionPanelOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-full flex flex-col p-8 overflow-y-auto">
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#264653] mb-2">
              Our Vision & Mission
            </h2>
            <div className="h-1 w-20 bg-[#264653] rounded-full" />
          </div>

          <div className="mb-8 flex-1">
            <h3 className="text-2xl font-semibold text-[#264653] mb-4">Mission</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              To empower healthcare through intelligent, connected, and data-driven
              technologies — making quality healthcare accessible, efficient, and
              personalized for every individual and organization.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-semibold text-[#264653] mb-4">Vision</h3>
            <p className="text-muted-foreground leading-relaxed">
              To build a healthier world where AI, innovation, and human empathy work
              together to redefine the future of healthcare.
            </p>
          </div>

          <div className="mt-auto pt-6 flex justify-center">
            <Button
              onClick={() => setVisionPanelOpen(false)}
              variant="default"
              size="lg"
              className="min-w-[200px]"
            >
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative bg-gradient-to-b from-background to-muted/40 border-t border-border mt-auto">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                <img
                  src="/WNTPL_Brief_Logo.svg"
                  alt="Wellnext"
                  className="h-11 w-auto object-contain"
                />
                <img
                  src="/WNTPL_Text_Logo.svg"
                  alt="Wellnext Technologies"
                  className="h-9 w-auto object-contain"
                />
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-md mb-6">
                Pioneering the future of healthcare through intelligent, connected
                technology. Building world-class, affordable health-tech products
                made in India for the world.
              </p>
              <div className="flex items-center gap-3">
                <a
                  href="#"
                  aria-label="LinkedIn"
                  className="h-9 w-9 grid place-items-center rounded-full border border-border text-muted-foreground hover:text-[#264653] hover:border-[#264653] transition-colors"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
                <a
                  href="#"
                  aria-label="Twitter"
                  className="h-9 w-9 grid place-items-center rounded-full border border-border text-muted-foreground hover:text-[#264653] hover:border-[#264653] transition-colors"
                >
                  <Twitter className="h-4 w-4" />
                </a>
                <a
                  href="#"
                  aria-label="GitHub"
                  className="h-9 w-9 grid place-items-center rounded-full border border-border text-muted-foreground hover:text-[#264653] hover:border-[#264653] transition-colors"
                >
                  <Github className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="md:col-span-3">
              <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-foreground/80">
                Quick Links
              </h3>
              <ul className="space-y-2.5">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="text-sm text-muted-foreground hover:text-[#264653] transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-2">
              <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-foreground/80">
                Contact
              </h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Mail className="h-4 w-4 mt-0.5 shrink-0 text-[#264653]" />
                  <a
                    href="mailto:corporate@wellnextek.com"
                    className="hover:text-[#264653] transition-colors break-all"
                  >
                    corporate@wellnextek.com
                  </a>
                </li>
                <li className="flex items-start gap-2">
                  <LifeBuoy className="h-4 w-4 mt-0.5 shrink-0 text-[#264653]" />
                  <Link
                    to="/support"
                    className="hover:text-[#264653] transition-colors"
                  >
                    Support
                  </Link>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#264653]" />
                  <span>India</span>
                </li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-foreground/80">
                Legal
              </h3>
              <ul className="space-y-2.5">
                <li>
                  <Link
                    to="/privacy-statement"
                    className="text-sm text-muted-foreground hover:text-[#264653] transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
            <p>
              &copy; {new Date().getFullYear()} Wellnext Technologies. All rights reserved.
            </p>
            <p className="text-xs uppercase tracking-wider">
              Built with care in India
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
